import { onBandCents } from '../vocal/constants.js'
import { wholeCents, type PhraseScore } from '../vocal/score-phrase.js'
import { relativeHeadline, relativeIntactCents } from './pitch-feedback.js'

/**
 * Pitch Trainer Phase 2's verdicts (plan M4; Bowie §3, Joni §7). Phase 1's `toPhraseFeedback` is
 * untouched: its tool keeps its own chain. Like it, this module only chooses lexicon entries and their
 * numbers; the UI composes no sentence.
 */

export interface PhraseVerdict {
  id: string
  tokens: Record<string, string | number>
}

export interface PhraseHeadlineContext {
  /**
   * The key change the phrase was moved by for this singer, −6 to +6: `fitPhrase`'s `keyChange`, never
   * its whole `semitones`, whose octaves the singer is not expected to follow (Bowie, #2491). 0 or absent
   * when the key did not change.
   */
  keyChange?: number
  /** "Moved from {{songKey}} to {{key}}": the keys' names, for the original-key verdict */
  songKey?: string
  practiceKey?: string
}

/** Too long to hold: at least this many reference notes, and missed ≥ max(2, ⌈30 %⌉) of them */
const tooLongMinNotes = 7
const tooLongMissedFraction = 0.3
const tooLongMinMissed = 2

/** Sag: at least 5 notes, a fitted change of 25 ¢ or more, starting within 15 ¢ of the pitch */
const sagMinNotes = 5
const sagMinChangeCents = 25
const sagStartToleranceCents = 15

/** Hardest leap: a minor 3rd or wider, more than 20 ¢ out */
const leapMinSemitones = 3
const leapErrorCents = 20

const distance = (semitones: number) => (semitones === 1 ? 'a semitone' : `${semitones} semitones`)

const intervalNames = [
  'unison',
  'minor 2nd',
  'major 2nd',
  'minor 3rd',
  'major 3rd',
  '4th',
  'tritone',
  '5th',
  'minor 6th',
  'major 6th',
  'minor 7th',
  'major 7th',
  'octave',
  'minor 9th',
  'major 9th',
  'minor 10th',
  'major 10th',
  '11th',
  'octave and a tritone',
  '12th',
  'minor 13th',
  'major 13th',
  'minor 14th',
  'major 14th',
  'two octaves',
]

/** An interval's full name, compound included ("major 6th", "major 10th"); either direction */
export const intervalName = (semitones: number): string =>
  intervalNames[Math.abs(semitones)] ?? `${Math.abs(semitones)} semitones`

/**
 * An interval name with its article, for copy that reads "a leap of {{aInterval}}": "an" before a vowel
 * sound (octave, 11th, augmented …), "a" otherwise. A plural name, "two octaves" or the "N semitones"
 * fallback, takes none (Bowie, #2491).
 */
export const withArticle = (name: string): string => {
  if (name.startsWith('two ') || name.endsWith(' semitones')) {
    return name
  }
  return `${/^(octave|11th|augmented)/.test(name) ? 'an' : 'a'} ${name}`
}

/** The least-squares line through the notes' cents: where it starts and how far it moves end to end */
const fitLine = (cents: number[]) => {
  const n = cents.length
  const meanX = (n - 1) / 2
  const meanY = cents.reduce((total, value) => total + value, 0) / n
  const covariance = cents.reduce((total, value, index) => total + (index - meanX) * (value - meanY), 0)
  const variance = cents.reduce((total, _value, index) => total + (index - meanX) ** 2, 0)
  const slope = variance === 0 ? 0 : covariance / variance
  return { start: meanY - slope * meanX, change: slope * (n - 1) }
}

/** Bowie §3: the whole phrase sank or crept, with every interval intact and a start on pitch */
const sagOf = (score: PhraseScore): PhraseVerdict | null => {
  const { perNote } = score.absolute
  const { perInterval } = score.relative
  if (perNote.length < sagMinNotes || !perInterval.every(interval => Math.abs(interval.cents) <= relativeIntactCents)) {
    return null
  }
  const { start, change } = fitLine(perNote.map(entry => entry.cents))
  const shown = wholeCents(change)
  if (shown < sagMinChangeCents || wholeCents(start) > sagStartToleranceCents) {
    return null
  }
  return { id: change < 0 ? 'vocal-drift-sag-under' : 'vocal-drift-sag-over', tokens: { cents: shown } }
}

const isTooLong = (score: PhraseScore) => {
  /** Forgiven repeats are notes of the phrase too (Quincy, #2440) */
  const notes = score.referenceIndices.length + score.missed.length + score.forgiven.length
  const enough = Math.max(tooLongMinMissed, Math.ceil(tooLongMissedFraction * notes))
  return notes >= tooLongMinNotes && score.missed.length >= enough
}

const isOnPitch = (score: PhraseScore) =>
  score.missed.length === 0 &&
  score.extra.length === 0 &&
  score.absolute.perNote.every(entry => entry.band !== 'wrong-note' && wholeCents(entry.cents) <= onBandCents)

/**
 * The one headline for a take, in Joni §7's order: too long → octave → original key → transposed →
 * sag → drift → on pitch → none. A phrase with any note off, missed or extra never reaches on pitch.
 */
export const phraseHeadline = (score: PhraseScore | null, context: PhraseHeadlineContext): PhraseVerdict | null => {
  if (!score) {
    return null
  }
  if (isTooLong(score)) {
    return { id: 'vocal-phrase-too-long', tokens: { count: score.missed.length } }
  }
  if (score.octave !== 0) {
    return { id: score.octave < 0 ? 'vocal-octave-under' : 'vocal-octave-over', tokens: {} }
  }
  const relative = relativeHeadline(score)
  const isTransposed = relative?.id.startsWith('vocal-good-relative-transposed') ?? false
  if (relative && isTransposed) {
    const keyChange = context.keyChange ?? 0
    const sungShift = (relative.id.endsWith('-under') ? -1 : 1) * Number(relative.tokens.semitones)
    if (keyChange !== 0 && sungShift === -keyChange) {
      /** The numbers are what the singer did */
      return {
        id: sungShift < 0 ? 'vocal-original-key-under' : 'vocal-original-key-over',
        tokens: {
          songKey: context.songKey ?? '',
          key: context.practiceKey ?? '',
          semitones: Math.abs(sungShift),
          distance: distance(Math.abs(sungShift)),
        },
      }
    }
    return relative
  }
  const sag = sagOf(score)
  if (sag) {
    return sag
  }
  if (relative) {
    return relative
  }
  return isOnPitch(score) ? { id: 'vocal-phrase-on-pitch', tokens: {} } : null
}

/**
 * The leap sung furthest out, for the hardest spot (Bowie §3): a minor 3rd or wider, more than 20 ¢
 * from its interval. Narrow when the sung leap is smaller than written, wide when it is bigger.
 *
 * @param names the reference notes' spelled names, index-aligned with the reference
 * @returns the verdict, and `notes`: the leap's two reference indices, for its Hear it (plan M6a step 6.4)
 */
export const hardestLeap = (
  score: PhraseScore,
  names: string[]
): (PhraseVerdict & { notes: [number, number] }) | undefined => {
  /**
   * Bowie, M6: the off-notes list already names a wrong note, and a leap from an on-pitch note is that one
   * note's error. Either would report the same error twice, so only a leap between two notes that are both
   * off, neither of them wrong, qualifies. Filtered before the worst is picked, so the next-worst can show.
   */
  const isOffNotWrong = (index: number) => {
    const note = score.absolute.perNote[index]
    return note !== undefined && note.band !== 'wrong-note' && wholeCents(note.cents) > onBandCents
  }
  const leaps = score.relative.perInterval.flatMap((interval, index) => {
    const isLeap = Math.abs(interval.expected) >= leapMinSemitones
    const cents = wholeCents(interval.cents)
    const isOwnError = isOffNotWrong(index) && isOffNotWrong(index + 1)
    return isLeap && isOwnError && cents > leapErrorCents ? [{ interval, cents, from: index, to: index + 1 }] : []
  })
  const [worst] = leaps.sort((a, b) => b.cents - a.cents)
  if (!worst) {
    return undefined
  }
  const isNarrow = Math.abs(worst.interval.sung) < Math.abs(worst.interval.expected)
  return {
    id: isNarrow ? 'vocal-leap-narrow' : 'vocal-leap-wide',
    tokens: {
      from: names[score.referenceIndices[worst.from]] ?? '',
      to: names[score.referenceIndices[worst.to]] ?? '',
      interval: intervalName(worst.interval.expected),
      aInterval: withArticle(intervalName(worst.interval.expected)),
      cents: worst.cents,
    },
    notes: [score.referenceIndices[worst.from], score.referenceIndices[worst.to]],
  }
}
